import { Component, h } from 'preact'
import Header from '../header'
import ButtonControl from '../button-control'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import Modal from '../../components/reactModal'
import AccordionPanel from '../panel/AccordionPanel'
import { RegistrationApprovalPanelBody, RegistrationApprovalPanelHeader } from './RegistrationApprovalPanel'
import { UsernameRestrictionsPanelBody, UsernameRestrictionsPanelHeader } from './UsernameRestrictionsPanel'

interface Props {
    registrationApprovalSetting: boolean,
    restrictPhoneNumberAsUsernameSetting: boolean,
    restrictWordsInUsernameSetting?: boolean,
    restrictedWords?: Array<string>
}
interface State {
    storedState: Props,
    isRegistrationApprovalEnabled: boolean,
    showRegistrationApprovalActionButtons: boolean,

    isRestrictPhoneNumberAsUsernameEnabled: boolean,
    isRestrictWordsInUsernameEnabled?: boolean,
    restrictedWords?: Array<string>,
    showValidationError: boolean,
    showUsernameRestrictionsActionButtons: boolean,

    messageType?: string|null,
    message?: string,
    hasUnsavedChanges: boolean,
    showUnsavedChangesModal: boolean,

    activePanel?: number|undefined
}

class RegistrationRules extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      storedState: {
        registrationApprovalSetting: this.props.registrationApprovalSetting,
        restrictPhoneNumberAsUsernameSetting: this.props.restrictPhoneNumberAsUsernameSetting,
        restrictWordsInUsernameSetting: this.props.restrictWordsInUsernameSetting ?? false,
        restrictedWords: this.props.restrictedWords ?? undefined
      },
      isRegistrationApprovalEnabled: this.props.registrationApprovalSetting,
      showRegistrationApprovalActionButtons: false,

      isRestrictPhoneNumberAsUsernameEnabled: this.props.restrictPhoneNumberAsUsernameSetting,
      isRestrictWordsInUsernameEnabled: this.props.restrictWordsInUsernameSetting ?? false,
      restrictedWords: this.props.restrictedWords ?? undefined,
      showValidationError: false,
      showUsernameRestrictionsActionButtons: false,

      hasUnsavedChanges: false,
      showUnsavedChangesModal: false
    }
    this.onRegistrationApprovalChanged = this.onRegistrationApprovalChanged.bind(this)
    this.saveRegistrationApprovalSetting = this.saveRegistrationApprovalSetting.bind(this)
    this.resetRegistrationApprovalSettings = this.resetRegistrationApprovalSettings.bind(this)

    this.onRestrictPhoneNumberAsUsernameSettingChanged = this.onRestrictPhoneNumberAsUsernameSettingChanged.bind(this)
    this.onRestrictWordsInUsernameSettingChanged = this.onRestrictWordsInUsernameSettingChanged.bind(this)
    this.onRestrictedWordsChanged = this.onRestrictedWordsChanged.bind(this)
    this.saveUsernameRestrictionSetting = this.saveUsernameRestrictionSetting.bind(this)
    this.resetUsernameRestrictionSettings = this.resetUsernameRestrictionSettings.bind(this)
    this.onFocusRestrictedWords = this.onFocusRestrictedWords.bind(this)

    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount () {
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => this.onUnload(e))
  }

  componentWillUnmount () {
    window.removeEventListener('beforeunload', this.onUnload)
  }

  onUnload (e: BeforeUnloadEvent) {
    if (this.checkPanelHasUnsavedChanges()) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  onRegistrationApprovalChanged (value: boolean) {
    let showActionButtons = this.state.storedState.registrationApprovalSetting !== value
    this.setState({
      isRegistrationApprovalEnabled: value,
      showRegistrationApprovalActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 1
    })
  }

  async saveRegistrationApprovalSetting () {
    this.setState({ messageType: null })
    const successMessage = `'New registration control' setting has been updated successfully.`
    const errorMessage = 'Something went wrong, setting can\'t be updated. Please try again.'
    try {
      await axios.post('/user/management/toggleRegistrationApproval', {
        requireApproval: this.state.isRegistrationApprovalEnabled
      })
      const updatedState = this.state.storedState
      updatedState.registrationApprovalSetting = this.state.isRegistrationApprovalEnabled

      this.setState({
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showRegistrationApprovalActionButtons: false
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: errorMessage,
        showRegistrationApprovalActionButtons: false
      })
    }
  }

  resetRegistrationApprovalSettings () {
    this.setState({
      isRegistrationApprovalEnabled: this.state.storedState.registrationApprovalSetting,
      showRegistrationApprovalActionButtons: false
    }
    )
  }

  onRestrictPhoneNumberAsUsernameSettingChanged (value: boolean) {
    let showActionButtons = this.state.storedState.restrictPhoneNumberAsUsernameSetting !== value
    this.setState({
      isRestrictPhoneNumberAsUsernameEnabled: value,
      showUsernameRestrictionsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 0
    })
  }

  onRestrictWordsInUsernameSettingChanged (value: boolean) {
    const { restrictWordsInUsernameSetting, restrictedWords } = this.state.storedState
    let showActionButtons = restrictWordsInUsernameSetting !== value || restrictedWords !== this.state.restrictedWords
    if (!value) {
      this.setState({ showValidationError: false })
    }
    this.setState({
      isRestrictWordsInUsernameEnabled: value,
      showUsernameRestrictionsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 0
    })
  }

  onFocusRestrictedWords () {
    this.setState({
      showUsernameRestrictionsActionButtons: true
    })
  }

  onRestrictedWordsChanged (value: string) {
    if (value === '') {
      this.setState({
        restrictedWords: [],
        hasUnsavedChanges: true,
        showValidationError: true,
        showUsernameRestrictionsActionButtons: true
      })
      return
    }

    let words = value.split(',').map(word => word.trim())
    let showActionButtons = this.state.storedState.restrictedWords !== words
    this.setState(
      {
        restrictedWords: words,
        showUsernameRestrictionsActionButtons: showActionButtons,
        hasUnsavedChanges: showActionButtons,
        showValidationError: false,
        activePanel: 0
      }
    )
  }

  async saveUsernameRestrictionSetting () {
    if (this.state.restrictedWords?.length === 0 && this.state.isRestrictWordsInUsernameEnabled === true) {
      this.setState({ showValidationError: true })
      return
    }
    this.setState({ messageType: null })
    const successMessage = `'Username restriction' settings have been updated successfully.`
    const errorMessage = 'Something went wrong, settings can\'t be updated. Please try again.'
    try {
      await axios.post('/user/management/usernameRestrictionsSetting', {
        restrictPhoneNumberAsUsername: this.state.isRestrictPhoneNumberAsUsernameEnabled,
        isRestrictWordsInUsernameEnabled: this.state.isRestrictWordsInUsernameEnabled,
        restrictedWords: this.state.restrictedWords
      })
      const updatedState = this.state.storedState
      updatedState.restrictedWords = this.state.restrictedWords
      updatedState.restrictWordsInUsernameSetting = this.state.isRestrictWordsInUsernameEnabled
      updatedState.restrictPhoneNumberAsUsernameSetting = this.state.isRestrictPhoneNumberAsUsernameEnabled
      this.setState({
        message: successMessage,
        messageType: 'success',
        showUsernameRestrictionsActionButtons: false,
        storedState: updatedState
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: errorMessage,
        showUsernameRestrictionsActionButtons: false
      })
    }
  }

  resetUsernameRestrictionSettings () {
    const { restrictPhoneNumberAsUsernameSetting, restrictWordsInUsernameSetting, restrictedWords } = this.state.storedState
    this.setState({
      isRestrictPhoneNumberAsUsernameEnabled: restrictPhoneNumberAsUsernameSetting,
      isRestrictWordsInUsernameEnabled: restrictWordsInUsernameSetting,
      restrictedWords: restrictedWords,
      showUsernameRestrictionsActionButtons: false,
      showValidationError: false
    })
  }

  closeModal () {
    this.setState({ showUnsavedChangesModal: false })
  }

  resetChanges () {
    const { registrationApprovalSetting, restrictPhoneNumberAsUsernameSetting, restrictWordsInUsernameSetting, restrictedWords } = this.state.storedState
    const hasRestrictPhoneNumberAsUsernameChanged = restrictPhoneNumberAsUsernameSetting !== this.state.isRestrictPhoneNumberAsUsernameEnabled
    const hasRestrictWordsInUsernameChanged = restrictWordsInUsernameSetting !== this.state.isRestrictWordsInUsernameEnabled
    const hasRestrictWordsListChanged = restrictedWords !== this.state.restrictedWords

    const usernameRestrictionsChanged = hasRestrictPhoneNumberAsUsernameChanged || hasRestrictWordsInUsernameChanged || hasRestrictWordsListChanged
    if (usernameRestrictionsChanged) {
      this.setState({ activePanel: 1 })
      this.resetUsernameRestrictionSettings()
    }

    if (registrationApprovalSetting !== this.state.isRegistrationApprovalEnabled) {
      this.setState({ activePanel: 0 })
      this.resetRegistrationApprovalSettings()
    }
    this.setState({ hasUnsavedChanges: false, showUnsavedChangesModal: false })
  }

  checkPanelHasUnsavedChanges () {
    const { registrationApprovalSetting, restrictPhoneNumberAsUsernameSetting, restrictWordsInUsernameSetting, restrictedWords } = this.state.storedState
    const hasRegistrationApprovalSettingsChanged = registrationApprovalSetting !== this.state.isRegistrationApprovalEnabled
    const hasRestrictPhoneNumberAsUsernameSettingsChanged = restrictPhoneNumberAsUsernameSetting !== this.state.isRestrictPhoneNumberAsUsernameEnabled
    const hasRestrictedWordsSettingChanged = restrictWordsInUsernameSetting !== this.state.isRestrictWordsInUsernameEnabled
    const hasRestrictedWordsChanges = restrictedWords !== this.state.restrictedWords

    return hasRegistrationApprovalSettingsChanged || hasRestrictPhoneNumberAsUsernameSettingsChanged || hasRestrictedWordsSettingChanged || hasRestrictedWordsChanges
  }

  render ({ registrationApprovalSetting }: Props, { storedState, isRegistrationApprovalEnabled, showRegistrationApprovalActionButtons, isRestrictPhoneNumberAsUsernameEnabled, showUsernameRestrictionsActionButtons, isRestrictWordsInUsernameEnabled = false, restrictedWords, showValidationError, messageType, message, hasUnsavedChanges, showUnsavedChangesModal, activePanel } : State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    let registrationApprovalStatusText = storedState.registrationApprovalSetting ? 'on' : 'off'
    let usernameRestrictionsStatusText = storedState.restrictPhoneNumberAsUsernameSetting || storedState.restrictWordsInUsernameSetting ? 'on' : 'off'
    let panelHasUnsavedChanges = this.checkPanelHasUnsavedChanges()

    let RegistrationApprovalPanel = {
      title: <RegistrationApprovalPanelHeader statusText={registrationApprovalStatusText} />,
      body: <RegistrationApprovalPanelBody isRegistrationApprovalEnabled={isRegistrationApprovalEnabled}
        onRegistrationApprovalChanged={() => this.onRegistrationApprovalChanged(!isRegistrationApprovalEnabled)}
        showRegistrationApprovalActionButtons={showRegistrationApprovalActionButtons}
        onSave={() => this.saveRegistrationApprovalSetting()}
        onCancel={() => this.resetRegistrationApprovalSettings()} />,
      hasUnsavedChanges: panelHasUnsavedChanges
    }
    let UsernameRestrictionsPanel = {
      title: <UsernameRestrictionsPanelHeader statusText={usernameRestrictionsStatusText} />,
      body: <UsernameRestrictionsPanelBody isRestrictPhoneNumberAsUsernameEnabled={isRestrictPhoneNumberAsUsernameEnabled}
        onRestrictPhoneNumberAsUsernameSettingChanged={() => this.onRestrictPhoneNumberAsUsernameSettingChanged(!isRestrictPhoneNumberAsUsernameEnabled)}
        isRestrictWordsInUsernameEnabled={isRestrictWordsInUsernameEnabled}
        onRestrictWordsInUsernameSettingChanged={() => this.onRestrictWordsInUsernameSettingChanged(!isRestrictWordsInUsernameEnabled)}
        restrictedWords={restrictedWords}
        onTextareaFocus={() => this.onFocusRestrictedWords()}
        onRestrictedWordsChanged={(value: string) => this.onRestrictedWordsChanged(value.trim())}
        showValidationError={showValidationError}
        showUsernameRestrictionsActionButtons={showUsernameRestrictionsActionButtons}
        onSave={() => this.saveUsernameRestrictionSetting()}
        onCancel={() => this.resetUsernameRestrictionSettings()} />,
      hasUnsavedChanges: panelHasUnsavedChanges
    }

    return (
      <div className='registration-rules-overview qa-registration-rules-overview'>
        <Header
          title='Registration Rules'
          disableCancelButton
          disableSaveButton
        />
        {
          messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
        }
        <AccordionPanel
          defaultActivePanelIndex={activePanel}
          panels={[UsernameRestrictionsPanel, RegistrationApprovalPanel]}
          renderUnsavedChangesModal={() => this.setState({ showUnsavedChangesModal: true })}
        />

        <Modal isOpen={showUnsavedChangesModal} onClose={() => this.closeModal()}>
          <div className='unsaved-changes-modal'>
            <h3 className='modal-title'>Unsaved Changes</h3>
            <div className='modal-description'>You have unsaved changes. Do you want to discard them?</div>
            <ButtonControl size='medium' variant='brand' newStyle title='Discard changes'
              onClick={() => this.resetChanges()} />
            <ButtonControl newStyle size='medium' title='Cancel' onClick={() => this.closeModal()} />
          </div>
        </Modal>
      </div>
    )
  }
}
export default RegistrationRules
